import React, { ReactElement, useMemo } from 'react';
import { Select } from '../../../atoms/Select';
import styled from '@emotion/styled';
import breakpoints from '../../../theme/material/breakpoints';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import OffersTutorialModal from '../../common/modals/OffersTutorialModal';
import { useDispatch } from 'react-redux';
import { updateShowTutorialOffer } from '../../../actions/profileActions';
import { GridView, ListView } from '../../../atoms/icons/favoriteIcons';
import { isDarkMode } from '../../../utils/helpers';
import { FONT_FAMILIES } from 'theme/material/constants';
import { getBackgroundTheme, getTextColor } from 'style';

const SubHeader = styled('div')({
  color: isDarkMode() ? '#AFB0B1' : '#212121',
  borderBottom: '1px solid #C9C9C9',
  paddingBottom: 10,
  flex: '1 0 100%',
  display: 'flex',
  alignItems: 'end',
  justifyContent: 'space-between',
  width: '100%',
  fontFamily: FONT_FAMILIES.secondary,
  fontSize: 24,
  lineHeight: '18px',
  letterSpacing: '-0.3px',
  [breakpoints.down('sm')]: {
    paddingLeft: 0,
    paddingRight: 0,
    alignItems: 'center',
    paddingTop: 5,
  },
});

const HiddenContainer = styled('div')({
  display: 'none',
});

const Box = styled.div<{ justifyContent?: string; alignItems: string; isLeft: boolean }>(props => ({
  display: 'flex',
  justifyContent: props.justifyContent,
  alignItems: props.alignItems,
  width: '100%',
  [breakpoints.down('sm')]: {
    paddingLeft: props.isLeft ? 10 : 0,
    paddingRight: props.isLeft ? 0 : 10,
  },
}));

const SubHeaderText = styled('span')(() => ({
  color: isDarkMode() ? '#8c8c8c' : '#212121',
  [breakpoints.down('xs')]: {
    display: 'none',
  },
}));

const VerticalLine = styled('span')({
  display: 'inline-block',
  borderLeft: '1px solid #DAE1E9',
  margin: '0 16px',
  height: 20,
  [breakpoints.down('sm')]: {
    display: 'none',
  },
});

const ViewActions = styled('a')({
  cursor: 'pointer',
  marginRight: 15,
  height: 22,
  width: 22,
  [breakpoints.down('sm')]: {
    marginRight: 15,
  },
});

interface IOfferSubHeaderProps {
  viewType: string;
  isOneTapModeVariation: boolean;
  handleViewSwitcher: () => void;
  pageTitle: string;
  changeOrder: (e: React.ChangeEvent<HTMLSelectElement>) => any;
  orderingType: string;
  showOfferTutorial: boolean;
  shouldShowOneTapOfferGridView: boolean;
}

const OfferSubHeader = ({
  viewType,
  isOneTapModeVariation,
  handleViewSwitcher,
  pageTitle,
  changeOrder,
  orderingType,
  showOfferTutorial,
  shouldShowOneTapOfferGridView,
}: IOfferSubHeaderProps): ReactElement => {
  const dispatch = useDispatch();
  const { isMobile } = useMediaBreakpoints();

  const memoizedOrderDropdown = useMemo(() => {
    return (
      <Select
        onChange={changeOrder}
        value={orderingType}
        baseSelectStyle={{
          width: '127px',
          height: '31px',
          borderRadius: '3px',
        }}
        customSelectStyle={{
          background: getBackgroundTheme().block,
        }}
        dropdownContainerCustomStyle={{
          height: '31px',
        }}
        customDropDownStyle={{
          height: 11,
          width: 92,
          padding: '9px 27px 9px 6px',
          color: getTextColor().LIGHT_GRAY.color,
          fontFamily: FONT_FAMILIES.primary,
          fontSize: '15px',
          fontWeight: 400,
          lineHeight: '11px',
          letterSpacing: '-0.3px',
          textAlign: 'right',
        }}
        noSpacing
      >
        <option value="recent" key="new">
          Newest First
        </option>
        <option value="oldest" key="old">
          Oldest First
        </option>
        <option value="highest" key="high">
          Highest Price
        </option>
        <option value="lowest" key="low">
          Lowest Price
        </option>
      </Select>
    );
  }, [changeOrder, orderingType]);

  const memoizeSubHeaderDetail = useMemo(() => {
    return (
      <Box alignItems={'flex-end'} isLeft={true}>
        <SubHeaderText data-test-id="offers-sub-header">{pageTitle}</SubHeaderText>
        <VerticalLine />
        <OffersTutorialModal
          onClose={() => showOfferTutorial && dispatch(updateShowTutorialOffer(false))}
          hideArrows
          clickableIndicators
          linkStyle={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            color: '#8392A7',
            lineHeight: '16px',
            letterSpacing: -0.3,
          }}
          svgIconFill={'#2B8FD7'}
        />
      </Box>
    );
  }, [showOfferTutorial, pageTitle]);

  return (
    <SubHeader style={{ visibility: shouldShowOneTapOfferGridView ? 'hidden' : 'visible' }}>
      {!isOneTapModeVariation ? (
        <>
          {memoizeSubHeaderDetail}
          <Box justifyContent={isMobile ? 'space-between' : 'flex-end'} alignItems="center" isLeft={false}>
            <ViewActions
              data-test-id="list-grid-view-btn"
              onClick={() => handleViewSwitcher()}
              aria-label={viewType === 'list' ? 'List' : 'Grid'}
            >
              {viewType === 'list' ? <GridView /> : <ListView />}
            </ViewActions>
            {memoizedOrderDropdown}
          </Box>
        </>
      ) : (
        // This line of code is wrapped in a HiddenContainer component.
        // Removing this line may cause an addEventListener error, not sure really why??
        <HiddenContainer> {memoizedOrderDropdown}</HiddenContainer>
      )}
    </SubHeader>
  );
};

export default React.memo(OfferSubHeader);
